import { HttpStatus } from '@bioliberty/http';
import { SessionSummary, User } from '@bioliberty/lifemind';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { TagPicker } from 'rsuite';
import { useGetPatientQuery, useGetPatientSessionsQuery, useGetUsersByIdsQuery } from '../../../api';
import { useGetLatestPatientLifeglovQuery } from '../../../api/lifeglovs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/Card';
import { dateRanges } from '../../../components/Input';
import { DateRangePickerInput } from '../../../components/Input/DateRangePickerInput';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/Tabs';
import { ActivityAvgRPEChart } from '../../../home/org-dashboard/charts/ActivityAvgRPEChart';
import { ActivitySessionTimeSeriesChart } from '../../../home/org-dashboard/charts/ActivitySessionTimeSeriesChart';
import { ActivityTypeBarChart } from '../../../home/org-dashboard/charts/ActivityTypeBarChart';
import { ActivityTypePieChart } from '../../../home/org-dashboard/charts/ActivityTypePieChart';
import { ActivityTypeTotalRepsTimeSeriesChart } from '../../../home/org-dashboard/charts/ActivityTypeTotalRepsTimeSeriesChart';
import { useDashboardInsights } from '../../../home/org-dashboard/hooks/useDashboardInsights';
import { useGranularityFormatters } from '../../../home/org-dashboard/hooks/useGranularityFormatters';
import { usePatientActivitySummaryStats } from '../../../home/org-dashboard/hooks/usePatientActivitySummaryStats';
import { NoActivityDataIndicator } from '../../../home/org-dashboard/NoActivityDataIndicator';
import { useActivityTypeChoices } from '../../../hooks/useActivityTypeChoices';
import { useDateRange } from '../../../hooks/useDateRange';
import { NotFound } from '../../../NotFound';
import { cn } from '../../../util';
import { formatSecondsString } from '../../../util/date';
import { formatUserName } from '../../../util/profile';
import { usePatientViewDataTableState } from '../../hooks/usePatientViewDataTableState';
import { DeletePatientModal } from './DeletePatientModal';
import { EditPatientModal } from './EditPatientModal';
import { PatientActivitySkeleton } from './PatientActivitySkeleton';
import { PatientDashboardSkeleton } from './PatientDashboardSkeleton';
import { PatientTopBar } from './PatientTopBar';
import { SessionsActivityTile } from './SessionsActivityTile';
import { SummaryStatistics } from './SummaryStatistics';

enum PatientTab {
  Insights = 'insights',
  Sessions = 'sessions'
}

const sessionsPageSize = 12;

export const PatientDetails = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(dateRanges.last30days.value);
  const [activityTypeIds, setActivityTypeIds] = useState<string[]>([]);
  const activityTypeChoices = useActivityTypeChoices();

  const { id } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || PatientTab.Sessions;

  const onTabChange = useCallback(
    (tab: string) => {
      setSearchParams({ tab });
    },
    [setSearchParams]
  );

  const { data: patient, isLoading: isPatientLoading, error } = useGetPatientQuery(id as string);

  const { data: patientLifeglov, isLoading: isPatientLifeglovLoading } = useGetLatestPatientLifeglovQuery(id as string);

  const {
    data: summaryStats,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching
  } = usePatientActivitySummaryStats({
    patientIds: [Number(id)],
    activityTypeIds,
    dateRange
  });

  const {
    activitySessionTimeSeriesData,
    activityTypeTotalRepsTimeSeriesData,
    activityTimeSeriesData,
    activityTypeData,
    granularity,
    isLoading: isInsightsLoading
  } = useDashboardInsights({
    patientIds: [Number(id)],
    dateRange,
    activityTypeIds
  });

  const { tickFormatter, titleFormatter } = useGranularityFormatters({ granularity });

  const [{ page }, _, setDataTablePage] = usePatientViewDataTableState({ page: 0 }, 'patientSessions');

  const { dateFrom, dateTo } = useDateRange(dateRange);

  const { data: sessionsData, isLoading: isSessionsLoading } = useGetPatientSessionsQuery({
    patientId: Number(id),
    dateFrom,
    dateTo,
    page: (page ?? 0) + 1,
    pageSize: sessionsPageSize
  });

  const clinicianIds = useMemo(() => uniqueClinicianIds(sessionsData?.items ?? []), [sessionsData]);

  const { data: clinicians = [], isLoading: isLoadingUsers } = useGetUsersByIdsQuery(
    clinicianIds.length === 0 ? skipToken : clinicianIds
  );

  const sessions = withClinicianNames(sessionsData?.items ?? [], clinicians);

  const sessionsPageCount = Math.ceil((sessionsData?.totalItems ?? 0) / sessionsPageSize);

  const isLoading =
    isPatientLoading ||
    isPatientLifeglovLoading ||
    isSummaryLoading ||
    isSessionsLoading ||
    isInsightsLoading ||
    isLoadingUsers;

  const hasActivityData = useMemo(() => {
    return Object.values(summaryStats || {}).some((stat) => stat?.value !== 0);
  }, [summaryStats]);

  if (error && 'status' in error && error.status === HttpStatus.NotFound) {
    return <NotFound />;
  }

  if (isLoading || !patient || !summaryStats) {
    return activeTab === PatientTab.Sessions ? <PatientActivitySkeleton /> : <PatientDashboardSkeleton />;
  }

  return (
    <Tabs defaultValue={activeTab} onValueChange={onTabChange} className="flex-1 flex-col">
      <PatientTopBar patient={patient} setShowEditModal={setShowEditModal} setShowDeleteModal={setShowDeleteModal} />

      <div className="flex flex-col items-center justify-between gap-4 pt-6 md:flex-row md:gap-0">
        <TabsList className="grid w-full grid-cols-2 md:w-[400px] md:min-w-[400px]">
          <TabsTrigger value={PatientTab.Sessions}>Sessions</TabsTrigger>
          <TabsTrigger value={PatientTab.Insights}>Patient Insights</TabsTrigger>
        </TabsList>

        <div className="flex flex-col items-center gap-4 md:flex-row">
          <TagPicker
            data={activityTypeChoices}
            onChange={setActivityTypeIds}
            placeholder="All activities"
            size="lg"
            searchable={false}
            className={cn('w-[350px]', activeTab !== PatientTab.Insights && 'hidden!')}
            data-testid="activity-type-input"
          />

          <DateRangePickerInput
            value={dateRange}
            onChange={setDateRange}
            className="w-[350px]"
            testId="date-range-input"
          />
        </div>
      </div>

      <TabsContent value={PatientTab.Sessions} className="flex w-full flex-col justify-center gap-4 py-5">
        <SessionsActivityTile
          patientId={Number(id)}
          sessions={sessions}
          dateRange={dateRange}
          page={page}
          pageSize={sessionsPageSize}
          pageCount={sessionsPageCount}
          onPaginationChange={(state) => setDataTablePage(state.pageIndex)}
        />
      </TabsContent>

      <TabsContent value={PatientTab.Insights} className="w-full">
        {!hasActivityData && <NoActivityDataIndicator className="mt-32" />}

        {hasActivityData && (
          <>
            <SummaryStatistics
              summaryStats={summaryStats}
              isLoading={isSummaryFetching}
              averageTotalRepsCompletedTitle="Total reps"
              averageTotalTimeTakenTitle="Total time on task"
            />
            <div className="flex flex-col justify-center gap-5 pb-5 md:flex-row">
              <Card className="md:w-2/3">
                <CardHeader>
                  <CardTitle>{titleFormatter('Session reps')}</CardTitle>
                  <CardDescription>The total number of repetitions completed per session</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivitySessionTimeSeriesChart
                    data={activitySessionTimeSeriesData}
                    dataKey="totalReps"
                    tickFormatter={tickFormatter}
                  />
                </CardContent>
              </Card>

              <Card className="md:w-1/3">
                <CardHeader>
                  <CardTitle>Avg reps by activity</CardTitle>
                  <CardDescription>The average number of repetitions in an activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivityTypeBarChart data={activityTypeData} dataKey="avgTotalReps" />
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col justify-center gap-5 pb-5 md:flex-row">
              <Card className="md:w-2/3">
                <CardHeader>
                  <CardTitle>{titleFormatter('Session time on task')}</CardTitle>
                  <CardDescription>The total duration of all activities in seconds per session</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivitySessionTimeSeriesChart
                    data={activitySessionTimeSeriesData}
                    dataKey="totalTimeTaken"
                    tickFormatter={tickFormatter}
                  />
                </CardContent>
              </Card>

              <Card className="md:w-1/3">
                <CardHeader>
                  <CardTitle>Avg time on task by activity</CardTitle>
                  <CardDescription>The average duration of an activity in minutes</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivityTypeBarChart
                    data={activityTypeData}
                    dataKey="avgTimeTaken"
                    labelFormatter={formatSecondsString}
                    tickFormatter={formatSecondsString}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col justify-center gap-5 pb-5 md:flex-row">
              <Card className="md:w-2/3">
                <CardHeader>
                  <CardTitle>{titleFormatter('Avg RPE')}</CardTitle>
                  <CardDescription>The average rate of perceived exertion</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivityAvgRPEChart
                    data={activityTimeSeriesData}
                    dataKey="avgFeedbackScore"
                    labelKey="timestamp"
                    tickFormatter={tickFormatter}
                  />
                </CardContent>
              </Card>

              <Card className="md:w-1/3">
                <CardHeader>
                  <CardTitle>Avg RPE by activity</CardTitle>
                  <CardDescription>The average rate of perceived exertion for an activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivityTypeBarChart data={activityTypeData} dataKey="avgFeedbackScore" />
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col justify-center gap-5 pb-5 md:flex-row">
              <Card className="w-full">
                <CardHeader>
                  <CardTitle>{titleFormatter('Reps by activity')}</CardTitle>
                  <CardDescription>The total number of repetitions for each activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivityTypeTotalRepsTimeSeriesChart
                    data={activityTypeTotalRepsTimeSeriesData}
                    tickFormatter={tickFormatter}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col justify-center gap-5 pb-5 md:flex-row">
              <Card className="w-full">
                <CardHeader>
                  <CardTitle>Activities by activity</CardTitle>
                  <CardDescription>The total number of activites for each activity type</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivityTypePieChart data={activityTypeData} dataKey="activityCount" />
                </CardContent>
              </Card>

              <Card className="w-full">
                <CardHeader>
                  <CardTitle>Reps by activity</CardTitle>
                  <CardDescription>The total number of repetitions for each activity type</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivityTypePieChart data={activityTypeData} dataKey="totalReps" />
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </TabsContent>

      <EditPatientModal
        oldPatient={patient}
        oldLifeglov={patientLifeglov}
        open={showEditModal}
        onOpenChange={setShowEditModal}
      />
      <DeletePatientModal patient={patient} open={showDeleteModal} onOpenChange={setShowDeleteModal} />
    </Tabs>
  );
};

const uniqueClinicianIds = (sessions: SessionSummary[]): string[] => {
  const ids = new Set<string>();
  for (const session of sessions) {
    const clinicianId = session.activities[0]?.clinicianId;
    if (clinicianId) {
      ids.add(clinicianId);
    }
  }
  return Array.from(ids).sort();
};

const withClinicianNames = (
  sessions: SessionSummary[],
  users: User[]
): Array<SessionSummary & { clinicianName: string }> => {
  return sessions.map((session) => {
    const clinicianId = session.activities[0]?.clinicianId;
    const user = users.find(({ id }) => id === clinicianId);
    return {
      ...session,
      clinicianName: user ? formatUserName(user) : ''
    };
  });
};
